import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { BulkDeactivateResult, BulkSkippedUser } from "../../../store/slices/UsersV3Slice";
import { bulkDeactivateUsersAsync } from "../../../store/thunks/UsersV3Thunk";
import { UserSelection } from "../../../services/UsersV3Service";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "../../Catalyst/dialog";
import { Button } from "../../Catalyst/button";
import { useToast } from "../../../contexts/ToastContext";
import ButtonSpinner from "./ButtonSpinner";
import SkippedUsersList from "./SkippedUsersList";
import { pluralizeUsers } from "./utils";

interface DeactivateUsersDialogProps {
  open: boolean;
  onClose: () => void;
  selection: UserSelection;
  selectedCount: number;
  // Called after the server accepted the deactivation (even partially) so the
  // page clears the selection and refetches the list and stats.
  onCompleted: () => void;
}

// The page remounts this dialog (new `key`) and clears the bulk request state on
// every open, so each deactivation starts fresh.
const DeactivateUsersDialog = ({ open, onClose, onCompleted, ...props }: DeactivateUsersDialogProps) => {
  // Frozen when the dialog opens (it is remounted on every open): the page
  // clears its selection on success, and the dialog must not re-render as
  // "Deactivate 0 users" while it fades out.
  const [{ selection, selectedCount }] = useState(() => props);
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccessToast } = useToast();
  const { bulkStatus, bulkError } = useSelector((state: RootState) => state.UsersV3Store);
  const [skipped, setSkipped] = useState<BulkSkippedUser[]>([]);
  const [batchId, setBatchId] = useState<string | null>(null);

  const isDeactivating = bulkStatus === STATUS.LOADING;

  // Ignore backdrop/Escape mid-request so the admin is never unsure whether it ran.
  const handleClose = () => {
    if (!isDeactivating) onClose();
  };

  const handleConfirm = async () => {
    if (isDeactivating) return;
    const result = await dispatch(bulkDeactivateUsersAsync(selection));
    // bulkError is rendered inline; keep the dialog open so the admin can retry.
    if (!bulkDeactivateUsersAsync.fulfilled.match(result)) return;

    const outcome = result.payload as BulkDeactivateResult;
    const skippedUsers = outcome?.skipped ?? [];
    if (outcome?.deactivatedCount > 0) {
      showSuccessToast("Users deactivated", `Deactivated ${pluralizeUsers(outcome.deactivatedCount)}.`);
    }
    onCompleted();
    if (skippedUsers.length > 0) {
      setSkipped(skippedUsers);
      setBatchId(outcome?.batchId ?? null);
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      {skipped.length > 0 ? (
        <>
          <DialogTitle>Some users were not deactivated</DialogTitle>
          <DialogBody>
            <SkippedUsersList title={`${pluralizeUsers(skipped.length)} skipped`} skipped={skipped} batchId={batchId} />
          </DialogBody>
          <DialogActions>
            <Button color="dark" onClick={handleClose}>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Deactivate {pluralizeUsers(selectedCount)}?</DialogTitle>
          <DialogDescription>
            Deactivated users can no longer sign in to Finley and are signed out on their next
            request. They are not notified by email. Their account data is retained, and each user
            can be reactivated individually from their profile.
          </DialogDescription>
          {bulkError && <p className="mt-4 text-sm font-medium text-red-500">{bulkError}</p>}
          <DialogActions>
            <Button outline onClick={handleClose} disabled={isDeactivating}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirm} disabled={isDeactivating}>
              {isDeactivating && <ButtonSpinner />}
              {isDeactivating ? "Deactivating..." : "Deactivate"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default DeactivateUsersDialog;
